import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface CameraCaptureProps {
  onCapture: (base64: string) => void;
  onClose: () => void;
}

export default function CameraCapture({ onCapture, onClose }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Camera access denied';
          setError(msg);
        }
      }
    }

    startCamera();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  const handleCapture = () => {
    if (!videoRef.current || !ready) return;

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    const base64 = dataUrl.split(',')[1];

    // Stop camera immediately
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    onCapture(base64);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {error ? (
        <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
          <p className="text-white text-lg font-semibold mb-2">Camera Unavailable</p>
          <p className="text-white/70 text-sm mb-6">{error}</p>
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-white/10 text-white rounded-full text-sm font-medium hover:bg-white/20 transition-colors"
          >
            Go Back
          </button>
        </div>
      ) : (
        <>
          {/* Camera feed */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />

          {/* Dark overlay with card cutout */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Top dark area */}
            <div className="w-full bg-black/60" style={{ height: 'calc(50% - 110px)' }} />
            {/* Middle row with card cutout */}
            <div className="flex" style={{ height: '220px' }}>
              <div className="w-6 bg-black/60" />
              <div className="flex-1 rounded-2xl border-2 border-white/40" />
              <div className="w-6 bg-black/60" />
            </div>
            {/* Bottom dark area */}
            <div className="w-full flex-1 bg-black/60" style={{ height: 'calc(50% - 110px)' }} />
          </div>

          {/* Top controls */}
          <div className="absolute top-12 left-0 right-0 flex items-center justify-between px-4 z-10">
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70 transition-colors"
            >
              <X size={20} className="text-white" strokeWidth={2} />
            </button>

            <span className="text-sm font-medium text-white/80">
              Position card within frame
            </span>

            {/* Spacer to balance the layout */}
            <div className="w-10" />
          </div>

          {/* Bottom controls */}
          <div className="absolute bottom-12 left-0 right-0 flex items-center justify-center z-10">
            <button
              onClick={handleCapture}
              disabled={!ready}
              className="w-20 h-20 rounded-full border-4 border-white flex items-center justify-center transition-opacity disabled:opacity-40"
            >
              <div className="w-16 h-16 rounded-full bg-white" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
